/**
 * Canvas onboarding coordination context.
 *
 * Tiny piece of shared state between the onboarding widget node (which
 * lives inside the canvas, in transformed space) and the canvas chrome
 * (toolbar, ReactFlow viewport — all in screen space). The widget acts
 * as the "director" and writes into this context to ask the chrome to:
 *
 *   • lock the viewport (no pan / zoom) so screen-space targets are
 *     stable relative to the widget,
 *   • highlight a specific UI element by stable id (the matching DOM
 *     node carries `data-onboarding-target="<id>"`),
 *
 * and to read whether the canvas explorer is currently open so steps
 * can advance when the user completes the requested action.
 *
 * Targets are referenced by id rather than ref / selector so the widget
 * doesn't import from the chrome and the chrome doesn't import from the
 * widget — they only share this context module.
 *
 * The widget node is also rendered as an invisible "state shell" — the
 * visible coach card is a screen-fixed overlay (`OnboardingOverlay`)
 * mounted at canvas root that reads `activeOnboarding` from this
 * context. The shell publishes the current step + callbacks here on
 * mount; unmounts clear it. This is what frees up the canvas as a
 * real drop / interaction surface during drop-heavy steps.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  DEFAULT_SAFE_INSETS,
  type SafeInsets,
} from "../nodes/widgets/onboarding-welcome/anchors";
import type {
  OnboardingData,
  StepDefinition,
} from "../nodes/widgets/onboarding-welcome/shared/types";

/**
 * Stable, brand-stable identifiers for elements that the onboarding
 * flow can spotlight. Add new entries here as new steps come online —
 * each entry must be matched by a `data-onboarding-target="<id>"`
 * attribute on the corresponding DOM node in the canvas chrome.
 */
export type OnboardingTargetId =
  | "agent-chat-toggle"
  | "canvas-object-tools"
  | "chat-send-button"
  | "artifact-publish-button"
  | "connections-entry";

/**
 * Pre-filled prompt injected into the canvas floating chat's composer
 * as `defaultValue`. When set, `CanvasSessionPaneContent` passes
 * `message` as `defaultValue` to `MessageInput`, hydrating Lexical
 * with editable text on mount. The user sees the prompt ready to send
 * (and can edit it before they do). Cleared when no step is pinning.
 *
 * Kept payload-only (no React deps) so this context stays free of
 * component imports.
 */
export interface OnboardingPinnedComposerPrompt {
  /** Stable id; steps listen for `cohesive:onboarding:composer-prompt-sent` + this id. */
  id: string;
  /** Message body Lexical hydrates into the composer on mount. */
  message: string;
}

/**
 * Pre-filled prompt for the next floating Quick Chat the user opens.
 * When set, `CanvasPage` injects `message` as the composer's initial
 * content the next time the user triggers Quick Chat (right-click
 * empty canvas → Quick Chat). The user is still the one performing
 * the gesture — onboarding doesn't open the composer programmatically
 * — but the message is waiting for them when they get there. Cleared
 * (`null`) when no onboarding step is asking for one.
 *
 * Kept payload-only here for the same reason as
 * `OnboardingPinnedSuggestedAction` — no React deps in shared state.
 */
export interface OnboardingPinnedQuickChatPrompt {
  /** Stable id (used by steps to key off in window-event channels if needed). */
  id: string;
  /** Message body Lexical hydrates into the composer on open. */
  message: string;
}

/**
 * Snapshot of the active onboarding step published by the invisible
 * state shell (`OnboardingWidget`) for the screen-docked overlay
 * (`OnboardingOverlay`) to render. The overlay consumes this rather
 * than reaching into Loro itself so multiple onboarding widgets on a
 * canvas (shouldn't happen in practice, but cheap to be defensive)
 * still produce a single deterministic overlay.
 */
export interface ActiveOnboarding {
  /** React Flow node id of the state shell that owns persisted state. */
  nodeId: string;
  /** Registry index of the current step (1-based, clamped). */
  step: number;
  /** Registry entry for the current step (key + component + anchor). */
  stepDef: StepDefinition;
  /** Loro-persisted onboarding data record. */
  data: OnboardingData;
  /** True while the orchestrator is playing the cross-step exit animation. */
  outgoing: boolean;
  /** Bumps every dev-only restart so the overlay can replay entrance keyframes. */
  restartCount: number;
  /** Forward to the active step's `onAdvance`. */
  advance: () => void;
  /** Forward to the active step's `onFinish`. */
  finish: () => void;
  /** Dev-only reset to step 1. */
  restart: () => void;
  /**
   * Opens (or reuses) onboarding's dedicated agent chat session via the
   * canvas chat FAB. Steps 5 and 7 spotlight `agent-chat-toggle` on that
   * control instead of a removed toolbar button.
   */
  openAgentChat: () => void;
}

interface CanvasOnboardingState {
  /**
   * When true, the canvas viewport (pan, zoom, node drag) should be
   * frozen so screen-space spotlights stay calibrated to canvas-space
   * widgets. Steps set this on mount and clear it on unmount.
   */
  lockedViewport: boolean;
  /**
   * Stable id of the canvas-chrome element to spotlight, or null when
   * nothing is highlighted. The OnboardingSpotlight component reads
   * this and finds the target by `[data-onboarding-target="<id>"]`.
   */
  highlightTargetId: OnboardingTargetId | null;
  /**
   * Monotonically increasing counter — bumping it triggers a one-shot
   * "look here!" emphasis animation on the current spotlight (a bigger,
   * brighter pulse than the steady breathe). Used by step components
   * when the user clicks an in-card instruction line, in case they
   * missed the subtle halo on the chrome element it points at.
   */
  highlightEmphasisTick: number;
  /**
   * Whether the canvas contents rail is open. Published by CanvasPage from
   * sidebar rail state so onboarding steps can react without importing layout.
   */
  isExplorerOpen: boolean;
  /**
   * Whether the canvas floating agent chat is currently open (any
   * session present in the floating-chat stack). Published by
   * CanvasPage from `useCanvasFloatingChat().currentSessionId`. Onboarding
   * steps watch it to advance after the user opens the chat for the first time.
   */
  isAgentChatOpen: boolean;
  /**
   * When set, the canvas floating chat's `MessageInput` hydrates
   * Lexical with `message` as its initial text (`defaultValue`). The
   * user can edit the text before sending. Cleared (`null`) when no
   * step is pinning a prompt. Steps listen for
   * `cohesive:onboarding:composer-prompt-sent` (with matching `id`)
   * to detect when the user actually sends.
   */
  pinnedComposerPrompt: OnboardingPinnedComposerPrompt | null;
  /**
   * When set, the next Quick Chat the user opens (via the empty-area
   * right-click → Quick Chat gesture) hydrates its composer with this
   * payload's `message`. CanvasPage threads the message through
   * `useQuickChat.handleStartQuickChat`'s `initialContent` argument,
   * which `MessageInput` flushes into Lexical on mount. Onboarding
   * still requires the user to perform the gesture themselves — we
   * just have a draft waiting once they get there. Cleared when no
   * step is pinning a prompt.
   */
  pinnedQuickChatPrompt: OnboardingPinnedQuickChatPrompt | null;
  /**
   * The active onboarding state shell's published snapshot, or null
   * when no shell is mounted. `OnboardingOverlay` reads this to know
   * which step component to render and where to dock. The shell
   * itself keeps the canonical Loro state — this is purely a
   * render-time projection.
   */
  activeOnboarding: ActiveOnboarding | null;
  /**
   * Pixels of canvas chrome along each viewport edge that the
   * screen-docked overlay should avoid. Exposed as state (rather than
   * derived inline) so future work can update it from real layout
   * measurements (explorer pane width, floating chat panel rect)
   * without touching the overlay's call sites. Defaults are a small
   * uniform inset that reads well on any viewport.
   */
  safeInsets: SafeInsets;
}

interface CanvasOnboardingContextValue {
  state: CanvasOnboardingState;
  /** Replace any subset of the state. */
  update: (patch: Partial<CanvasOnboardingState>) => void;
}

const DEFAULT_STATE: CanvasOnboardingState = {
  lockedViewport: false,
  highlightTargetId: null,
  highlightEmphasisTick: 0,
  isExplorerOpen: false,
  isAgentChatOpen: false,
  pinnedComposerPrompt: null,
  pinnedQuickChatPrompt: null,
  activeOnboarding: null,
  safeInsets: DEFAULT_SAFE_INSETS,
};

const CanvasOnboardingContext =
  createContext<CanvasOnboardingContextValue | null>(null);

export function CanvasOnboardingProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [state, setState] = useState<CanvasOnboardingState>(DEFAULT_STATE);

  const update = useCallback((patch: Partial<CanvasOnboardingState>) => {
    setState((prev) => {
      // Cheap shallow-equality skip so steady-state writes don't churn
      // subscribers.
      let changed = false;
      for (const k of Object.keys(patch) as (keyof CanvasOnboardingState)[]) {
        if (patch[k] !== prev[k]) {
          changed = true;
          break;
        }
      }
      if (!changed) return prev;
      return { ...prev, ...patch };
    });
  }, []);

  const value = useMemo(() => ({ state, update }), [state, update]);

  return (
    <CanvasOnboardingContext.Provider value={value}>
      {children}
    </CanvasOnboardingContext.Provider>
  );
}

/**
 * Read-only access to the onboarding state. Returns the default state
 * when used outside the provider (rather than throwing) so the
 * onboarding flow degrades gracefully on canvases that don't host it.
 */
export function useCanvasOnboardingState(): CanvasOnboardingState {
  return useContext(CanvasOnboardingContext)?.state ?? DEFAULT_STATE;
}

/**
 * Mutator hook for steps and chrome to update onboarding state.
 * Returns a no-op when used outside the provider.
 */
export function useCanvasOnboardingUpdate(): (
  patch: Partial<CanvasOnboardingState>,
) => void {
  const ctx = useContext(CanvasOnboardingContext);
  return ctx?.update ?? (() => {});
}
